import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Trash2, Eye, EyeOff, GripVertical, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import AdminSidebar from '../components/layout/AdminSidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProjectsPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (isLoggedIn) {
      setIsAuthenticated(true);
      setCheckingAuth(false);
      fetchProjects();
      return;
    }
    
    const token = localStorage.getItem('adminToken');
    const expires = localStorage.getItem('adminTokenExpires');

    if (!token || !expires) {
      navigate('/admin/login');
      return;
    }

    if (new Date(expires) < new Date()) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminTokenExpires');
      navigate('/admin/login');
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/admin/validate`, { token });
      setIsAuthenticated(true);
      fetchProjects();
    } catch (error) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminTokenExpires');
      navigate('/admin/login');
    } finally {
      setCheckingAuth(false);
    }
  };

  const fetchProjects = async () => {
    try {
      // Use lightweight summary endpoint for faster loading
      const res = await axios.get(`${BACKEND_URL}/api/projects/list/summary`);
      const sorted = res.data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setProjects(sorted);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }
    try {
      await axios.delete(`${BACKEND_URL}/api/projects/${projectId}`);
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const handleToggleVisibility = async (projectId, currentStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/projects/${projectId}/toggle-visibility`);
      // Update local state
      setProjects(prev => prev.map(p => 
        p.id === projectId ? { ...p, is_published: !currentStatus } : p
      ));
    } catch (error) {
      console.error('Error toggling visibility:', error);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    const newProjects = [...projects];
    const [draggedItem] = newProjects.splice(draggedIndex, 1);
    newProjects.splice(dropIndex, 0, draggedItem);
    
    const updatedProjects = newProjects.map((project, idx) => ({
      ...project,
      order: idx
    }));
    
    setProjects(updatedProjects);
    setDraggedIndex(null);
    setDragOverIndex(null);
    
    await saveProjectOrder(updatedProjects);
  };

  const saveProjectOrder = async (orderedProjects) => {
    setIsSaving(true);
    try {
      const reorderData = orderedProjects.map((project, idx) => ({
        id: project.id,
        order: idx
      }));
      
      await axios.put(`${BACKEND_URL}/api/projects/reorder/bulk`, {
        projects: reorderData
      });
    } catch (error) {
      console.error('Error saving project order:', error);
      await fetchProjects();
    } finally {
      setIsSaving(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-slate-600">Checking authentication...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AdminSidebar pageTitle="Portfolio Projects" pageIcon={Building2}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-sm text-slate-500">
            <GripVertical className="w-4 h-4 inline mr-1" />
            Drag rows to reorder projects
            {isSaving && <span className="ml-2 text-cyan-600">Saving...</span>}
          </p>
        </div>
        <Button 
          onClick={() => navigate('/admin/projects/new')} 
          className="bg-cyan-600 hover:bg-cyan-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Project
        </Button>
      </div>

      {/* Projects List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 w-10"></th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Image</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Project</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Category</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-slate-500">Loading...</td>
              </tr>
            ) : projects.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-slate-500">
                  No projects yet. Add your first project!
                </td>
              </tr>
            ) : (
              projects.map((project, index) => (
                <tr 
                  key={project.id} 
                  className={`border-b border-slate-100 hover:bg-slate-50 cursor-move transition-colors ${
                    dragOverIndex === index ? 'bg-cyan-50 border-cyan-300' : ''
                  }`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  <td className="py-3 px-4">
                    <GripVertical className="w-4 h-4 text-slate-400" />
                  </td>
                  <td className="py-3 px-4">
                    {project.featured_image ? (
                      <img 
                        src={project.featured_image} 
                        alt={project.name}
                        className="w-16 h-12 object-cover rounded"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-16 h-12 bg-slate-200 rounded flex items-center justify-center text-slate-400 text-xs">
                        No image
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-medium text-slate-900">{project.name}</div>
                    <div className="text-sm text-slate-500 truncate max-w-xs">
                      {project.description?.substring(0, 60)}...
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">
                    {project.categories && project.categories.length > 0 
                      ? project.categories.join(', ') 
                      : '-'}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleToggleVisibility(project.id, project.is_published !== false)}
                        title={project.is_published !== false ? 'Hide from portfolio' : 'Show on portfolio'}
                        className={project.is_published === false ? 'text-slate-400' : 'text-green-600'}
                      >
                        {project.is_published !== false ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/admin/projects/${project.id}`)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(project.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </AdminSidebar>
  );
};

export default AdminProjectsPage;
